'use client';

import React, { useRef, useState } from 'react';
import { twMerge } from 'tailwind-merge';
import Heading from '@/components/common/Heading';
import { usePageTheme } from '@/stores/page-theme';
import { IconSliderData } from '@/types/iconSlider';
import { MdPlayArrow, MdPause } from 'react-icons/md';
import {
  Splide,
  SplideSlide,
  type SplideOptions,
} from '@splidejs/react-splide';
import { AutoScroll } from '@splidejs/splide-extension-auto-scroll';
import '@splidejs/react-splide/css';
import '@/styles/splide-slider.scss';
import IconComponent from '@/components/FlashCards/Icon';
import Image from 'next/image';
import { ThemeColor, getThemeColor } from '@/theme/themeColors';

interface IconSliderProps extends IconSliderData {}

export const IconSlider: React.FC<IconSliderProps> = ({
  title = '',
  headingSize = 'h3',
  headingColor,
  shadowText = false,
  background = 'inherit',
  backgroundColor,
  backgroundImage,
  theme = 'default',
  slides = [],
  bgColor,
  mainIcon,
  iconBGColor = '#CFECFF',
  iconTextColor = '#1C8EFF',
  iconImage,
  buttonBGColor = '#FFFFFF',
  buttonTextColor = '#1C8EFF',
}) => {
  const { bgColor: themeBgColor } = usePageTheme();
  const [isPlaying, setIsPlaying] = useState(true);
  const splideRef = useRef<any>(null);

  const validSlides = slides.filter((slide) => slide && slide.text);

  const getBackgroundStyle = () => {
    if (background === 'solid' && backgroundColor) {
      return { backgroundColor };
    } else if (background === 'gradient-mesh') {
      return {
        background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
      };
    } else if (backgroundImage) {
      return {
        backgroundImage: `url(${backgroundImage})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      };
    }
    return {};
  };

  const getTextColor = () => {
    if (headingColor) return `text-[${headingColor}]`;
    if (theme && theme !== 'default') {
      const themeColor = getThemeColor(theme as ThemeColor);
      switch (themeColor) {
        case 'purple':
          return 'text-white';
        case 'black':
          return 'text-white';
        default:
          return 'text-[#282A2B]';
      }
    }
    return 'text-[#282A2B]';
  };

  const getHeadingSize = (size: string | undefined): number | '40px' => {
    if (!size) return 3;
    if (size === '40px') return '40px';
    return parseInt(size.replace('h', ''));
  };

  const togglePlayPause = () => {
    if (!splideRef.current) return;

    const autoScroll = splideRef.current.Components?.AutoScroll;
    if (!autoScroll) return;

    if (isPlaying) {
      autoScroll.pause();
    } else {
      autoScroll.play();
    }
    setIsPlaying(!isPlaying);
  };

  const splideOptions: SplideOptions = {
    type: 'loop',
    drag: 'free' as const,
    focus: 'center',
    perPage: 2,
    perMove: 1,
    gap: '2rem',
    arrows: false,
    pagination: false,
    autoScroll: {
      speed: 1,
      pauseOnHover: false,
      pauseOnFocus: false,
      rewind: false,
      autoStart: true,
    },
    waitForTransition: true,
    updateOnMove: true,
    trimSpace: true,
    clone: true,
    cloneStatus: true,
    padding: { left: '10%', right: '10%' },
    speed: 1000,
    easing: 'cubic-bezier(0.25, 1, 0.5, 1)',
    breakpoints: {
      1024: {
        perPage: 2,
        padding: { left: '5%', right: '5%' },
      },
      768: {
        perPage: 2,
        padding: { left: '2%', right: '2%' },
      },
      640: {
        perPage: 1,
        padding: { left: '0', right: '0' },
      },
    },
  };

  if (!validSlides.length) {
    return null;
  }

  return (
    <div className='icon-slider-section relative py-16'>
      <div
        className={`icon-slider-container container mx-auto max-w-7xl rounded-2xl px-4 pb-8 pt-24 ${
          backgroundColor
            ? `bg-[${backgroundColor}]`
            : bgColor || themeBgColor || ''
        }`}
        style={getBackgroundStyle()}
      >
        {title && (
          <div className='icon-slider-header mb-20 text-center'>
            <div className='mx-auto flex max-w-2xl flex-col items-center justify-center gap-y-6'>
              {(mainIcon || iconImage) && (
                <div
                  className='icon-slider-main-icon mb-4 flex h-16 w-16 items-center justify-center rounded-full'
                  style={{
                    backgroundColor: iconBGColor,
                    color: iconTextColor,
                  }}
                >
                  {iconImage ? (
                    <Image src={iconImage} alt={title} width={36} height={36} />
                  ) : mainIcon?.name ? (
                    <IconComponent
                      name={mainIcon.name}
                      className='size-9'
                      fill={iconTextColor}
                    />
                  ) : null}
                </div>
              )}
              <Heading
                size={getHeadingSize(headingSize)}
                className={twMerge('font-bold', getTextColor())}
              >
                {title}
              </Heading>
            </div>
          </div>
        )}

        <div className='icon-slider-content relative mx-auto max-w-5xl'>
          <Splide
            options={splideOptions}
            extensions={{ AutoScroll }}
            onMounted={(splide) => {
              splideRef.current = splide;
            }}
            className='icon-slider-carousel'
          >
            {validSlides.map((slide, index) => (
              <SplideSlide
                key={slide.id || index}
                className='icon-slider-slide'
              >
                <div className='flex flex-col items-center p-4'>
                  <div
                    className={twMerge(
                      'icon-slider-slide-icon mb-4 flex h-12 w-12 items-center justify-center rounded-full',
                      `bg-[${slide.iconBGColor || '#CFECFF'}]`,
                      `text-[${slide.iconTextColor || '#1C8EFF'}]`
                    )}
                  >
                    {slide.iconImage ? (
                      <Image
                        src={slide.iconImage}
                        alt={slide.text}
                        width={28}
                        height={28}
                      />
                    ) : slide.icon?.name ? (
                      <IconComponent
                        name={slide.icon.name}
                        className='size-7'
                        fill={slide.iconTextColor || '#1C8EFF'}
                      />
                    ) : null}
                  </div>
                  <p
                    className={twMerge(
                      'icon-slider-slide-text max-w-[200px] break-words text-center text-[22px] font-bold leading-6  text-[#282A2B]',
                      slide.whiteText ? 'text-white' : ''
                    )}
                  >
                    {slide.text}
                  </p>
                </div>
              </SplideSlide>
            ))}
          </Splide>

          <div className='icon-slider-controls mt-10 flex justify-end'>
            <button
              className='icon-slider-play-pause flex items-center justify-center rounded-full p-2 shadow-md hover:bg-gray-100'
              style={{
                backgroundColor: buttonBGColor,
                color: buttonTextColor,
              }}
              type='button'
              onClick={togglePlayPause}
            >
              {isPlaying ? (
                <MdPause className='h-6 w-6' />
              ) : (
                <MdPlayArrow className='h-6 w-6' />
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
